import i18n from "@/lib/i18n";
import { deleteWebPushSubscription, getVapidStatus, saveWebPushSubscription } from "./api";
import { getToken, resolveApiUrl } from "@/lib/api";

const SW_PATH = "/sw-web-push.js";
let sessionCleanup: Promise<void> = Promise.resolve();

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export async function registerWebPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register(SW_PATH);
}

export async function enableWebPush(publicKey?: string): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (typeof window === "undefined" || !("Notification" in window) || !("PushManager" in window) || !("serviceWorker" in navigator)) {
    return { ok: false, reason: i18n.t("profilePage.webPush.unsupported") };
  }
  const token = getToken();
  const changedAccount = () => !token || getToken() !== token;
  const changedReason = { ok: false as const, reason: i18n.t("profilePage.webPush.accountChanged") };
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: i18n.t("profilePage.webPush.permissionDenied") };
  await sessionCleanup;
  if (changedAccount()) return changedReason;
  const vapidKey = publicKey || (await getVapidStatus()).public_key;
  if (!vapidKey) return { ok: false, reason: i18n.t("profilePage.webPush.notConfigured") };
  if (changedAccount()) return changedReason;
  const registered = await registerWebPushServiceWorker();
  if (!registered) return { ok: false, reason: i18n.t("profilePage.webPush.serviceWorkerFailed") };
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (changedAccount()) return changedReason;
  const subscription = existing ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: Uint8Array.from(urlBase64ToUint8Array(vapidKey)),
  });
  if (changedAccount()) {
    await subscription.unsubscribe();
    return changedReason;
  }
  const saved = await saveWebPushSubscription(subscription);
  if (!saved.configured) {
    await subscription.unsubscribe().catch(() => undefined);
    return { ok: false, reason: i18n.t("profilePage.webPush.serverNotConfigured") };
  }
  return { ok: true };
}

export async function disableWebPush(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    const results = await Promise.allSettled([deleteWebPushSubscription(subscription.endpoint), subscription.unsubscribe()]);
    const failed = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failed) throw failed.reason;
  }
}

/** Capture the old identity before setSession replaces its token. Never refresh or replay under the new account. */
export function clearWebPushForSessionChange(previousToken: string): Promise<void> {
  sessionCleanup = sessionCleanup.catch(() => undefined).then(async () => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    const results = await Promise.allSettled([
      fetch(resolveApiUrl("/v1/web-push/subscriptions"), {
        method: "DELETE",
        headers: { "content-type": "application/json", Authorization: `Bearer ${previousToken}` },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
        signal: AbortSignal.timeout(10000),
      }),
      subscription.unsubscribe(),
    ]);
    const browserResult = results[1];
    if (browserResult.status === "rejected") throw browserResult.reason;
  });
  return sessionCleanup;
}

export async function clearWebPushOnLogout(): Promise<void> {
  await disableWebPush();
}
